import TYPES from "@/core/services/Changelogs/types";

import Utils                 from '@/core/utils/Utils';
import BaseEntity            from '@/core/misc/BaseEntity';
import DateSimple            from '@/core/types/DateSimple';
import Title_ru_RU           from '@/core/misc/decorators/Title_ru_RU';
import serializeSimplified   from '@/core/misc/decorators/SerializeSimplified';

import type DemandV2         from '@/domains/DemandsV2/DemandV2';
import type DemandOption     from '@/domains/DemandsV2/DemandOption';
import Title_ru_RU_Class     from "@/core/misc/decorators/Title_ru_RU_Class";
import Qty from "@/core/types/Qty";
import Sum from "@/core/types/Sum";
import Core from "@/core/Core";
import CurrenciesService from "@/domains/Currencies/CurrenciesService";

/**
 * Заявка на поставку внутри потребности DemandV2.
 * У одной потребности может быть несколько заявок, каждая со своей датой
 * получения и желаемым количеством.
 * Симметрично с бэком: DemandRequest extends BaseEntity.
 */
@Title_ru_RU_Class('Заявка')                        // demandRequest → DemandRequest
class DemandRequest extends BaseEntity {

	static Events = {
		Created: 'DemandRequest:created',
		Updated: 'DemandRequest:updated',
		Deleted: 'DemandRequest:deleted',
	};

	/** Желаемое кол-во товара */
	@Title_ru_RU('желаемое кол-во', TYPES.NUMBER)
	protected _qtyDesired: Qty | null = null;

	/** Кол-во уже заказанное по этой заявке */
	public qtyOrdered: Qty | null = null;

	/** Кол-во осталось заказать */
	public qtyLeft: Qty | null = null;

	/** Дата к которой нужно поставить товар */
	@Title_ru_RU('желаемая дата получения', TYPES.DATE)
	protected _desiredReceiptDate: DateSimple | null = null;

	/** Обратная ссылка на потребность */
	@serializeSimplified
	public demand: DemandV2;

	static isDemandRequest(value: unknown): value is DemandRequest {
		return value instanceof DemandRequest;
	}

	static fromServer(demandRequestData: any, result: DemandRequest = new DemandRequest()): DemandRequest {
		super.fromServer(demandRequestData, result);

    	result._desiredReceiptDate = DateSimple.fromServer(demandRequestData.desiredReceiptDate);

    	result._qtyDesired   = Core.utils.hydrate(demandRequestData.qtyDesired, Qty.fromServer);
    	result.qtyOrdered    = Core.utils.hydrate(demandRequestData.qtyOrdered, Qty.fromServer);
    	result.qtyLeft       = Core.utils.hydrate(demandRequestData.qtyLeft, Qty.fromServer);

		return result;
	}

	/** ================ Геттеры/сеттеры ================ */

	set qtyDesired(value: Qty | null) {
		this._qtyDesired = value;

		this.demand.calculateDesiredQty();
	}

	get qtyDesired(): Qty | null {
		return this._qtyDesired;
	}

	set desiredReceiptDate(value: DateSimple | null) {
		this._desiredReceiptDate = value;

		this.demand.calculateMinDesiredReceiptDate();
	}

	get desiredReceiptDate(): DateSimple | null {
		return this._desiredReceiptDate;
	}

	/** ================ Расчёты ================ */

	/**
	 * Использованные опции и их распределение по этой заявке.
	 *
	 * Геттер, а не поле: величина производна от распределения по всей
	 * потребности, на сервере не хранится и в запрос не уходит. Само
	 * распределение считает потребность — заявки набирают количество по
	 * порядку, и результат для этой заявки зависит от того, сколько взяли
	 * заявки до неё.
	 */
	get usedOptions(): Array<{ option: DemandOption; usedQty: Qty }> {
		return this.demand.distribution.get(this) ?? [];
	}

	/**
	 * Сумма по заявке от поставщика в валюте потребности.
	 *
	 * На сервере такого поля нет: это вывод в интерфейсе, а не свойство
	 * заявки. Геттер, а не поле: курс читается из реактивного кеша, и при
	 * его появлении значение пересчитается само. Присвоенное в поле, оно
	 * осталось бы с тем курсом, что был на момент расчёта.
	 *
	 * Нет курса хотя бы по одной опции — расчёт прекращается: частичная
	 * сумма выглядела бы верной, будучи занижённой. Ошибку здесь не пишем:
	 * геттер зовётся на каждую перерисовку, в том числе до прихода курсов.
	 */
	get sumFullSeller(): Sum | null {
		if (this.demand.hasOptions === false || this.qtyDesired === null) {
			// Считать не из чего: это не нулевая сумма, а её отсутствие
			return null;
		}
		else {
			/** Стартовое значение — Sum.zero(), а не 0: складываем суммами,
			 *  нативный + на Numeric запрещён (valueOf кидает). Округление до
			 *  двух знаков вшито в Sum, прежний round10 до трёх не нужен. */
			let result = Sum.zero();

			for (const { option, usedQty } of this.usedOptions) {
				const sum = this.getSumFullSeller(option, usedQty);

				if (sum === null) {
					return null;
				}
				else {
					result = result.plus(sum);
				}
			}

			return result;
		}
	}

  /**
   * Сумма по одной опции, приведённая к валюте потребности.
   * null — посчитать нельзя: валюта неизвестна либо курса нет в кеше.
   */
  getSumFullSeller(option: DemandOption, qty: Qty): Sum | null {
    if (option.offerCurrencyCode === this.demand.currencyCode) {
      return option.priceFullSeller.mul(qty);
    }
    else {
      /** Регистрируем потребность в курсе: геттер зовётся из отрисовки, и
       *  без этого курс не приедет, если рядом нет вывода через
       *  formatInDisplayCurrency. Повторы отсекаются внутри сервиса. */
      CurrenciesService.requestRate(option.offerCurrencyCode, option.getDateForCurrencyRate());

      const priceInDemandCurrency = option.priceFullSeller.convertSync(
        option.offerCurrencyCode,
        this.demand.currencyCode,
        option.getDateForCurrencyRate(),
      );

      /** null — валюта неизвестна, пустое значение — курса нет в кеше.
       *  Прежний Sum.zero() занижал итог по заявке, не давая об этом знать. */
      if (priceInDemandCurrency === null || priceInDemandCurrency.isEmpty() === true) {
        return null;
      }
      else {
        return priceInDemandCurrency.mul(qty);
      }
    }
  }
}

export default DemandRequest;
